import { z } from 'zod';

import { durationCalculationMethodSchema } from '../../utils/durationsFromServices';
import { removeUndefinedValues } from '../../utils/iteration';
import { useAxleMutation, useAxleQuery } from '../apiHelpers';
import { organizationBasicSchema } from '../sharedSchemas/basicSchemas';
import { serviceBasicSchema, serviceForListDisplaySchema } from './services';

//////////////////////////////////////////////////////////////////////////////
// region Schemas & types for service bundle positions.
//////////////////////////////////////////////////////////////////////////////

const serviceBundlePositionSchema = z.object({
    uuid: z.string().uuid(),
    is_virtual: z.boolean(),
    start_offset_minutes: z.number().nullable(),
    duration_override_minutes: z.number().nullable(),
    duration_minutes: z.number(),
    services: z.array(serviceForListDisplaySchema),
});
export type ServiceBundlePosition = z.infer<typeof serviceBundlePositionSchema>;

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Schemas & types for service bundles themselves.
//////////////////////////////////////////////////////////////////////////////

export const serviceBundleBasicSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string(),
    organization: organizationBasicSchema,
});
export type ServiceBundleBasic = z.infer<typeof serviceBundleBasicSchema>;

export const serviceBundleForListSchema = serviceBundleBasicSchema.extend({
    services: z.array(serviceBasicSchema),
});
export type ServiceBundleForListDisplay = z.infer<typeof serviceBundleForListSchema>;

export const serviceBundleDetailsSchema = serviceBundleForListSchema.extend({
    description: z.string(),
    duration_override_minutes: z.number().nullable(),
    duration_minutes: z.number(),
    positions: z.array(serviceBundlePositionSchema),
});
export type ServiceBundleDetails = z.infer<typeof serviceBundleDetailsSchema>;

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Finally, the endpoints!
//////////////////////////////////////////////////////////////////////////////

export const useServiceBundles = () =>
    useAxleQuery({
        queryKey: ['serviceBundles', 'all'],
        path: '/service-bundles',
        suspense: true,
        responseSchema: z.object({
            service_bundles: z.array(serviceBundleForListSchema),
        }),
    }).data.service_bundles;

export const useServiceBundleDetails = (service_bundle_uuid: string) =>
    useAxleQuery({
        queryKey: ['serviceBundles', service_bundle_uuid],
        path: `/service-bundles/${service_bundle_uuid}`,
        suspense: true,
        responseSchema: z.object({
            service_bundle: serviceBundleDetailsSchema,
        }),
    }).data.service_bundle;

export const useServiceBundleOptions = () =>
    useAxleQuery({
        queryKey: ['serviceBundles', 'options'],
        path: '/service-bundles/options',
        suspense: true,
        responseSchema: z.object({
            organization_options: z.array(organizationBasicSchema),
            duration_calculation_method_by_org_id: z.record(durationCalculationMethodSchema),
        }),
    }).data;

export const useCreateServiceBundle = () =>
    useAxleMutation({
        axiosConfig: (data: {
            org_uuid: string;
            name: string;
            description: string;
            duration_override_minutes: number | null;
            positions: {
                is_virtual: boolean;
                start_offset_minutes: number | null;
                duration_override_minutes: number | null;
                service_uuids: string[];
            }[];
        }) => ({
            method: 'post',
            path: '/service-bundles/create',
            data: data,
        }),
        softResetQueries: [['serviceBundles']],
    });

export const useEditServiceBundle = (service_bundle_uuid: string) =>
    useAxleMutation({
        axiosConfig: ({
            org_uuid,
            name,
            description,
            duration_override_minutes,
            positions_to_create,
            positions_to_edit,
            positions_to_delete,
        }: {
            org_uuid?: string;
            name?: string;
            description?: string;
            duration_override_minutes?: number | null;
            positions_to_create: {
                is_virtual: boolean;
                start_offset_minutes: number | null;
                duration_override_minutes: number | null;
                service_uuids: string[];
            }[];
            positions_to_edit: {
                uuid: string;
                is_virtual?: boolean;
                start_offset_minutes?: number | null;
                duration_override_minutes?: number | null;
                service_uuids?: string[];
            }[];
            positions_to_delete: string[];
        }) => ({
            method: 'post',
            path: `/service-bundles/${service_bundle_uuid}/edit`,
            data: {
                ...removeUndefinedValues({
                    org_uuid: org_uuid,
                    name: name,
                    description: description,
                    duration_minutes: duration_override_minutes,
                }),
                positions_to_create: positions_to_create,
                positions_to_edit: positions_to_edit.map(removeUndefinedValues),
                positions_to_delete: positions_to_delete,
            },
        }),
        softResetQueries: [['serviceBundles']],
        hardResetQueries: [['serviceBundles', service_bundle_uuid]],
    });

export const useDeleteServiceBundle = (service_bundle_uuid: string) =>
    useAxleMutation({
        axiosConfig: () => ({
            method: 'post',
            path: `/service-bundles/${service_bundle_uuid}/delete`,
        }),
        softResetQueries: [['serviceBundles']],
    });

// endregion
